import {useMutation} from "@apollo/client";
import {yupResolver} from "@hookform/resolvers/yup";
import classNames from "classnames";
import {MutableRefObject, useCallback, useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import toast from "react-hot-toast";
import {FiArrowLeft, FiThumbsUp} from "react-icons/fi";
import * as Yup from "yup";

import {Button} from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {Chip} from "@/components/chip/Chip.tsx";
import {Divider} from "@/components/divider/Divider.tsx";
import {RadioField} from "@/components/form/fields/RadioField/RadioField.tsx";
import {TextAreaField} from "@/components/form/fields/TextAreaField.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {IconButton} from "@/components/iconButton/IconButton.tsx";
import {GQL_IS_IT_DOWN_SERVICE_QUERY, GQL_PUBLIC_PROFILES_DOWN_REPORT} from "@/lib/queries/isitdown.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {PublicProfilesDownCategory, PublicProfilesDownReportResponse} from "@/lib/types/isitdown.ts";

import styles from "./TopReportedIssues.module.scss";

const EXPIRES_IN = 24 * 60 * 60 * 1000; // 24 hours in milliseconds - synced with the BE

interface CategoryWithExpiry {
  [categoryId: number]: number; // categoryId -> expiresAt timestamp
}

interface DownReportFormValues {
  category_id: number;
  other_description?: string;
}

interface TopReportedIssuesProps {
  categories: PublicProfilesDownCategory[];
  profileId: string;
  downReportRef: MutableRefObject<HTMLDivElement | null>;
  showForm: boolean;
  setShowForm: (show: boolean) => void;
  showSuccess: boolean;
  setShowSuccess: (show: boolean) => void;
}

export const TopReportedIssues = ({
  categories,
  profileId,
  downReportRef,
  showForm,
  setShowForm,
  showSuccess,
  setShowSuccess,
}: TopReportedIssuesProps) => {
  const storageLocation = `public-profiles-pressed-down-categories-${profileId}`;
  const [pressedCategories, setPressedCategories] = useState((): Set<number> => {
    try {
      const stored = localStorage.getItem(storageLocation);
      if (!stored) return new Set();

      const parsedData: CategoryWithExpiry = JSON.parse(stored);
      const now = Date.now();
      const validCategories = new Set<number>();
      const updatedData: CategoryWithExpiry = {};

      // Check each category's individual expiry
      Object.entries(parsedData).forEach(([categoryId, expiresAt]) => {
        const id = parseInt(categoryId);
        if (now < expiresAt) {
          validCategories.add(id);
          updatedData[id] = expiresAt;
        }
      });

      // Update localStorage with only non-expired categories
      if (Object.keys(updatedData).length !== Object.keys(parsedData).length) {
        localStorage.setItem(storageLocation, JSON.stringify(updatedData));
      }

      return validCategories;
    } catch {
      return new Set();
    }
  });

  const setStoredData = useCallback(
    (pressedCategories: Set<number>) => {
      try {
        const stored = localStorage.getItem(storageLocation);
        const existingData: CategoryWithExpiry = stored ? JSON.parse(stored) : {};
        const now = Date.now();
        const updatedData: CategoryWithExpiry = {};

        // Keep existing non-expired categories
        Object.entries(existingData).forEach(([categoryId, expiresAt]) => {
          if (now < expiresAt) {
            updatedData[parseInt(categoryId)] = expiresAt;
          }
        });

        // Add new categories with fresh expiry
        pressedCategories.forEach((categoryId) => {
          if (!updatedData[categoryId]) {
            updatedData[categoryId] = now + EXPIRES_IN;
          }
        });
        if (Object.keys(updatedData).length === 0) {
          localStorage.removeItem(storageLocation);
        } else {
          localStorage.setItem(storageLocation, JSON.stringify(updatedData));
        }
      } catch (error) {
        console.error("Failed to save to localStorage:", error);
      }
    },
    [storageLocation]
  );

  useEffect(() => {
    setStoredData(pressedCategories);
  }, [pressedCategories, setStoredData]);

  const [createDownReport, {loading}] = useMutation<PublicProfilesDownReportResponse>(GQL_PUBLIC_PROFILES_DOWN_REPORT, {
    refetchQueries: [GQL_IS_IT_DOWN_SERVICE_QUERY],
  });

  const methods = useForm<DownReportFormValues>({
    resolver: yupResolver<DownReportFormValues>(
      Yup.object().shape({
        category_id: Yup.number().required("Category is required"),
        other_description: Yup.string().optional(),
      })
    ),
  });

  const onSubmit = async (data: DownReportFormValues) => {
    if (pressedCategories.has(data.category_id)) {
      toast.error("You have already reported this issue.");
      return;
    }
    try {
      const response = await createDownReport({
        variables: {
          data: {
            profile_id: profileId,
            ...data,
          },
        },
      });
      const result = response.data?.publicProfilesDownReport;
      if (result?.status === ResponseStatus.SUCCESS) {
        setPressedCategories((prev) => new Set(prev).add(data.category_id));
        if (showForm) {
          setShowSuccess(true);
          methods.reset();
        }
      } else if (result?.status === ResponseStatus.ERROR) {
        toast.error("Failed to report issue.");
      }
    } catch (error) {
      console.error("Error reporting issue:", error);
      toast.error("An unexpected error occurred while reporting the issue.");
    }
  };

  return (
    <div className={styles.container}>
      <div className="marketingTextS2Bold">User Reported Issues</div>
      <Card variant="marketingWhite" className={styles.card}>
        <div className={styles.cardBody} ref={downReportRef}>
          {showForm ? (
            <>
              <div className="pulseTextH2Bold text-start">Report an Issue</div>
              <Divider className="mt-3 mb-4" />
              {showSuccess ? (
                <div className={styles.boxBody}>
                  <div className="pulseTextBody pulse-color-gray-800">Thank you for submitting a report!</div>
                  <Button
                    variant="linkGreen"
                    size="fitContent"
                    onClick={() => {
                      setShowSuccess(false);
                      setShowForm(false);
                    }}
                  >
                    <Icon icon={FiArrowLeft} />
                    BACK TO REPORTS
                  </Button>
                </div>
              ) : (
                <FormProvider {...methods}>
                  <form onSubmit={methods.handleSubmit(onSubmit, console.error)} className={styles.formContainer}>
                    <div className={styles.boxBody}>
                      {categories.map(({id, name}) => (
                        <RadioField
                          key={id}
                          variant="marketing"
                          name="category_id"
                          label={name}
                          value={id}
                          disabled={pressedCategories.has(id)}
                          className={styles.radioOption}
                          subField={
                            name.toLowerCase() === "other" && id === Number(methods.watch("category_id")) ? (
                              <TextAreaField
                                name="other_description"
                                placeholder="Describe the issue"
                                className="mt-3"
                                rows={4}
                              />
                            ) : undefined
                          }
                        />
                      ))}
                    </div>
                    <Button
                      className="w-100 mt-4 flex-shrink-0"
                      variant="marketingPrimary"
                      size="marketingLarge"
                      type="submit"
                      disabled={!methods.watch("category_id") || loading}
                    >
                      Report an Issue
                    </Button>
                  </form>
                </FormProvider>
              )}
            </>
          ) : (
            <>
              <div className="text-start">
                <div className="pulseTextH2Bold">Top Reported Issues</div>
                <div className="pulseTextBodySmall pulse-color-gray-700 mt-3">
                  View and upvote commonly reported issues to verify service status.
                </div>
              </div>
              <Divider className="mt-3 mb-4" />
              <div className={classNames(styles.boxBody, "flex-grow-1")}>
                {categories.map(({id, name, count}) => {
                  return (
                    <div className={styles.issueContainer} key={id}>
                      <div className={styles.issue}>
                        <div className="pulseTextBodySmallBold">{name}</div>
                        <Chip variant="tag">{count} Reported</Chip>
                      </div>
                      <IconButton
                        variant="ghost"
                        size="fitIcon"
                        icon={FiThumbsUp}
                        className="pulse-color-gray-900"
                        onClick={() => onSubmit({category_id: id})}
                        disabled={loading || pressedCategories.has(id)}
                      />
                    </div>
                  );
                })}
              </div>
              <Button
                className="w-100 mt-4 flex-shrink-0"
                variant="marketingPrimary"
                size="marketingLarge"
                disabled={loading}
                onClick={() => setShowForm(true)}
              >
                Report an Issue
              </Button>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};
